import { invariant } from '@/shared/utils/typeAssertions'
import { watchOnce } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, markRaw, ref, type Ref } from 'vue'
import { useFileUploadPersistence } from '../../data/Uploads/useFileUploadPersistence'

type BaseUploadItem = {
  file: File
  fileName: string
  workspaceId: string
  projectId: string
  propertyId: string
  parentEntityId: string | null
  entityId: string
  progress: number
  dbRecordId?: string
}

type PreparingUploadItem = BaseUploadItem & {
  status: 'ready' | 'getting-url'
  url?: string
}

type UploadingUploadItem = BaseUploadItem & {
  status: 'uploading' | 'confirming' | 'done'
  url: string
}

type FailedUploadItem = BaseUploadItem & {
  status: 'error'
  error: string
  url?: string
}

type UploadItem = PreparingUploadItem | UploadingUploadItem | FailedUploadItem

type PendingItem = {
  id: string
  file: File
  workspaceId: string
  projectId: string
  propertyId: string
  parentEntityId: string | null
  status: 'idle' | 'creating-entity'
  dbRecordId?: string
}

type CommonUploadProps = {
  workspaceId: string
  projectId: string
  propertyId: string
  parentEntityId: string | null
}

export type FieldUploadProps = CommonUploadProps & { entityId: string }
export type PendingUploadProps = CommonUploadProps & { filename: string }

const getHash = ({
  entityId,
  projectId,
  propertyId,
  workspaceId,
  parentEntityId,
}: FieldUploadProps) => {
  return `${workspaceId}-${projectId}-${entityId}-${propertyId}-${parentEntityId || ''}`
}

const pendingId = ({
  filename,
  projectId,
  propertyId,
  workspaceId,
  parentEntityId,
}: PendingUploadProps) => {
  return `${workspaceId}-${projectId}-${propertyId}-${filename}-${parentEntityId || ''}`
}

export const useFieldUpload = defineStore('fieldUpload', () => {
  const uploads = ref(new Map<string, UploadItem>())
  const persistence = useFileUploadPersistence()

  watchOnce(
    () => persistence.isLoaded.value,
    async () => {
      if (!persistence.isSupported()) return

      await persistence.cleanOldFiles()

      for (const item of persistence.incompleteUploads.value) {
        invariant(item.id)
        const file = await persistence.getFileFromOpfs(item.opfsPath)

        if (!file) {
          persistence.removeRecord(item.id)
          continue
        }

        const props = {
          workspaceId: item.workspaceId,
          projectId: item.projectId,
          propertyId: item.propertyId,
          parentEntityId: item.parentEntityId || null,
        }
        if (item.entityId) {
          await addUpload({ ...props, entityId: item.entityId }, file, item.id)
        } else {
          await addPendingUploads(props, [file], item.id)
        }
      }
    },
  )

  /**
   * Adds a new file to be uploaded to the given cell of the given entity
   * This upload is in the initial ready state, and the next step is to fire
   * a request to the start upload endpoint to the backend.
   */
  const addUpload = async (specifier: FieldUploadProps, file: File, dbRecordId?: string) => {
    const hash = getHash(specifier)

    if (!dbRecordId) {
      const newDbRecordId = await persistence.storeNewUpload(specifier, file)
      if (newDbRecordId) {
        dbRecordId = newDbRecordId
      }
    }

    uploads.value.set(hash, {
      ...specifier,
      file: markRaw(file),
      fileName: file.name,
      progress: 0,
      status: 'ready',
      dbRecordId,
    })
  }

  /**
   * Retrieve the assigned upload object for the given cell of the given entity
   */
  const getUpload = (props: FieldUploadProps) => {
    return uploads.value.get(getHash(props))
  }

  /**
   * Change state of the given upload object to "starting".
   * This means the request to start the upload has been fired to the backend.
   * Once a response is received, the next step is to call `setUploadUploading`
   * and actually upload the file.
   */
  const setUploadGettingUrl = async (specifier: FieldUploadProps) => {
    const upload = getUpload(specifier)
    if (upload) {
      const status = 'getting-url'
      upload.status = status
      if (upload.dbRecordId) {
        await persistence.updateRecordStatus(upload.dbRecordId, status)
      }
    }
  }

  /**
   * Change state of the given upload object to "uploading".
   * This means we got the url to upload to and have started the upload.
   * We will now be calling the setProgress action until done
   */
  const setUploadUploading = async (specifier: FieldUploadProps, url: string) => {
    const upload = getUpload(specifier)
    if (upload) {
      const status = 'uploading'
      upload.status = status
      upload.url = url
      if (upload.dbRecordId) {
        await persistence.updateRecordWithUrl(upload.dbRecordId, url)
      }
    }
  }

  /**
   * Set upload progress of the given upload object
   */
  const setUploadProgress = async (specifier: FieldUploadProps, progress: number) => {
    const upload = getUpload(specifier)
    if (upload) {
      upload.progress = progress
      if (upload.dbRecordId) {
        await persistence.updateRecordProgress(upload.dbRecordId, progress)
      }
    }
  }

  /**
   * Put the given upload into confirming status. This means the actual file upload is done,
   * and we're now in the process of telling the backend this is the case.
   *
   * After we get a response, the next step is to mark the upload process as fully done.
   */
  const setUploadConfirming = async (specifier: FieldUploadProps) => {
    const upload = getUpload(specifier)
    if (upload) {
      const status = 'confirming'
      upload.status = status
      if (upload.dbRecordId) {
        await persistence.updateRecordStatus(upload.dbRecordId, status)
      }
    }
  }

  /**
   * Mark the upload as fully done. The next step is to clear it from the store.
   */
  const setUploadDone = async (specifier: FieldUploadProps) => {
    const upload = getUpload(specifier)
    if (!upload) return

    const status = 'done'
    upload.status = status

    if (upload.dbRecordId) {
      await persistence.removeRecord(upload.dbRecordId)
    }
  }

  /**
   * Put the given upload into an error state.
   *
   * The error string is currently a free-form string. Once we have proper error handling,
   * we should make it an enumerated set of codes, so the UI can consistently rely on them.
   */
  const setUploadError = async (specifier: FieldUploadProps, error: string) => {
    const upload = getUpload(specifier)
    if (!upload) {
      return
    }
    const hash = getHash(specifier)
    // because an errored upload is of a different type, we have to re-assign it
    uploads.value.set(hash, {
      file: markRaw(upload.file),
      fileName: upload.fileName,
      status: 'error',
      progress: 0,
      error,
      ...specifier,
      dbRecordId: upload.dbRecordId,
    })
    await removeDBAndFile(specifier)
  }

  /**
   * Remove an upload object from the store
   */
  const removeUpload = async (specifier: FieldUploadProps) => {
    const hash = getHash(specifier)
    await removeDBAndFile(specifier)
    uploads.value.delete(hash)
  }

  const removeDBAndFile = async (props: FieldUploadProps) => {
    const hash = getHash(props)
    const item = uploads.value.get(hash)
    if (!item || !item.dbRecordId) return

    await persistence.removeRecord(item.dbRecordId)
  }

  const nextReadyUpload = computed(() =>
    Array.from(uploads.value.values()).find((upload) => ['ready'].includes(upload.status)),
  )

  const nextDoneUpload = computed(() =>
    Array.from(uploads.value.values()).find((upload) => ['done'].includes(upload.status)),
  )

  const pendingUploads = ref(new Map<string, PendingItem>())
  const unprocessedPendingUploads: Ref<PendingItem[]> = ref([])

  const addPendingUploads = async (
    specifier: Omit<PendingUploadProps, 'filename'>,
    files: File[],
    uploadRecordId?: string,
  ) => {
    const pendingItems = files.map((file) => {
      const hash = pendingId({ ...specifier, filename: file.name })
      return {
        ...specifier,
        filename: file.name,
        file,
        status: 'idle' as const,
        id: hash,
        dbRecordId: uploadRecordId,
      }
    })

    unprocessedPendingUploads.value.push(...pendingItems)

    pendingItems.forEach(async (pendingItem) => {
      let recordId = pendingItem.dbRecordId
      if (!recordId) {
        recordId = await persistence.storeNewPendingUpload(pendingItem, pendingItem.file)
      }

      pendingUploads.value.set(pendingItem.id, {
        ...pendingItem,
        dbRecordId: recordId,
      })
      unprocessedPendingUploads.value.shift()
    })
  }

  const nextPendingUpload = computed(() => {
    return Array.from(pendingUploads.value.values()).find((upload) => upload.status === 'idle')
  })

  /**
   * Mark the pending upload associated with the given id as "creating-entity".
   *
   * This means an agent entity (row) is being created for it.
   * Once that is done, the pending upload will be upgraded to a proper upload.
   */
  const setPendingUploadCreatingEntity = async (id: string) => {
    const pendingUpload = pendingUploads.value.get(id)
    if (pendingUpload) {
      const status = 'creating-entity'
      pendingUpload.status = status
      if (pendingUpload.dbRecordId) {
        await persistence.updateRecordStatus(pendingUpload.dbRecordId, status)
      }
    }
  }

  /**
   * Convert the given pending upload into a proper upload by associating it
   * with the given entity id and moving it to the upload map.
   */
  const upgradePendingUpload = async (id: string, entityId: string) => {
    const pendingUpload = pendingUploads.value.get(id)
    if (!pendingUpload) {
      return
    }

    const specifier: FieldUploadProps = {
      workspaceId: pendingUpload.workspaceId,
      projectId: pendingUpload.projectId,
      entityId,
      propertyId: pendingUpload.propertyId,
      parentEntityId: pendingUpload.parentEntityId,
    }

    if (pendingUpload.dbRecordId) {
      await persistence.updateRecordEntityId(pendingUpload.dbRecordId, entityId)
    }
    pendingUploads.value.delete(id)
    await addUpload(specifier, pendingUpload.file, pendingUpload.dbRecordId)
  }

  /**
   * Retrieve pendign upload by agent id, property id and filename
   */
  const getPendingUpload = (specifier: PendingUploadProps) => {
    return pendingUploads.value.get(pendingId(specifier))
  }

  const inProgressUploads = computed(() => {
    const processed = Array.from(uploads.value.values())
    const pending = Array.from(pendingUploads.value.values())

    return [...processed, ...unprocessedPendingUploads.value, ...pending].filter((upload) => {
      return upload.status !== 'done' && upload.status !== 'error'
    })
  })

  return {
    addPendingUploads,
    addUpload,
    getPendingUpload,
    getUpload,
    nextDoneUpload,
    nextPendingUpload,
    nextReadyUpload,
    removeUpload,
    setUploadConfirming,
    setUploadDone,
    setUploadError,
    setUploadGettingUrl,
    setUploadProgress,
    setUploadUploading,
    setPendingUploadCreatingEntity,
    upgradePendingUpload,
    inProgressUploads,
    unprocessedPendingUploads,
  }
})
